import React from 'react';
import { Icon, Toggle, ToastContext, TooltipHelp, WeezHeader } from '@weezevent/nacre';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import RatesTreeModel from '../../../../models/ratesTreeModel';

import ProductSaleModel from '../../../../models/productSaleModel';
import EventModel from '../../../../models/eventModel';
import QuotasModel from '../../../../models/quotasModel';
import RatesManager, { generateClassNameRateName } from '../../../rates/components/rateManager';
import { Card as NacreCard } from '../../../../components/card';
import { RateStatus } from '../../../rates/components/rateStatus';
import { InheritedProperty } from '../../../rates/ratesList';
import StyleRM from '../../../rates/components/rateManager.module.css';
import { RateCategorySeat } from '../../../rates/components/rateCategorySeat';
import { RateBundleInfo } from '../../../rates/components/rateBundleInfo';
import { TableCell, TableRow } from 'semantic-ui-react';
import Style from './activationRatesList.module.css';

export default function ActivationRatesList({ organization, productId, eventId }) {
    const category = null;
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);

    const [eventProductForSale, setEventProductForSale] = React.useState({
        category: category,
        event: eventId,
        id: null,
        is_mandatory_product: false,
        is_opened_product: false,
        is_product_in_sale: false,
        product: productId,
        rate: null
    });
    const [ratesProductForSale, setRatesProductForSale] = React.useState([]);
    const [ratesArray, setRatesArray] = React.useState([]);
    const [disableRateRows, setDisableRateRows] = React.useState(new Set());
    const [eventRateToggleDisabled, setEventRateToggleDisabled] = React.useState(false);

    const [event, loadingEvent] = EventModel.useApiModel({
        id: eventId,
        organizationId: organization.id
    });

    const [quotas, loadingQuotas] = QuotasModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: eventId,
            getForRates: true,
            query: {
                limit: 9999,
                query: { id_date: 0 }
            }
        },
        [organization]
    );

    const [, loadForSale, sync] = ProductSaleModel.useApiModel(
        {
            organizationId: organization.id,
            productId,
            eventId,
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                setRatesProductForSale(
                    response.reduce((acc, rpfs) => {
                        if (rpfs.rate !== null) {
                            acc[rpfs.rate] = { ...rpfs.object };
                        }
                        return acc;
                    }, {})
                );

                let findEventProductForSale = response.find(f => f.rate === null);
                if (findEventProductForSale) {
                    setEventProductForSale({ ...findEventProductForSale.object });
                }
            }
        },
        [organization, productId, category]
    );

    const [, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: eventId,
            query: {
                expand: 'relays',
                has_distributor: false
            },
            //we check that the eventId isn't a key already in the allRates (to avoid requesting data we already have)
            onSuccess: _rates => {
                setRatesArray(_rates);
            }
        },
        [organization]
    );

    const postMEV = React.useCallback(
        (field, checked, rateIds) => {
            const rateIdList = Array.isArray(rateIds) ? rateIds : [rateIds];

            // We iterate and resolve each promise for each rate in rateIds
            const ratePromises = rateIdList.map(rateId => {
                let _rpfs = ratesProductForSale[rateId];
                let isCreate = Boolean(!_rpfs);

                // Check if the current value matches the target value for an existing record
                if (!isCreate && _rpfs[field] === checked) {
                    // If the field value is already set to the target 'checked' value,
                    // we return a resolved promise immediately to skip the API call.
                    return null;
                }

                const defaultRpfs = {
                    category,
                    event: eventId,
                    product: productId,
                    rate: rateId
                };

                // Create case
                if (isCreate) {
                    _rpfs = {
                        ...defaultRpfs,
                        ...{
                            id: null,
                            is_mandatory_product: eventProductForSale.is_mandatory_product,
                            is_opened_product: eventProductForSale.is_opened_product,
                            is_product_in_sale: eventProductForSale.is_product_in_sale
                        }
                    };

                    //We need to disable the row if it is a creation because
                    // we need the return of the pk, generated by menhir.
                    setDisableRateRows(currentState => {
                        currentState.add(rateId);
                        return new Set(Array.from(currentState));
                    });
                } else {
                    _rpfs = { ...defaultRpfs, ...{ id: _rpfs.id } };
                }

                _rpfs = { ..._rpfs, [field]: checked };

                return new ProductSaleModel(_rpfs)
                    .save({
                        organizationId: organization.id,
                        productId: productId
                    })
                    .then(result => {
                        if (isCreate) {
                            //Update pk
                            _rpfs.id = result.response.id;
                        }
                        setRatesProductForSale(current => ({
                            ...current,
                            [rateId]: { ...current[rateId], ..._rpfs }
                        }));
                    })
                    .finally(() => {
                        setDisableRateRows(currentState => {
                            currentState.delete(rateId);
                            return new Set(Array.from(currentState));
                        });
                    });
            });

            Promise.all(ratePromises)
                .then(() => {
                    // we don't wan't to send the request if not all rates have succeeded
                    // Rate updates succeeded, now check condition for event update
                    // Verify if all toggle are check or not for this field
                    // const allRatesToggleAreChecked = false
                    const allRatesToggleAreChecked = ratesArray.rates_list.every(rate => {
                        if (rateIdList.includes(rate.id)) {
                            return checked;
                        }
                        if (ratesProductForSale[rate.id]) {
                            return ratesProductForSale[rate.id][field];
                        }
                        return eventProductForSale[field];
                    });
                    if ((!checked && eventProductForSale[field]) || allRatesToggleAreChecked) {
                        // We update allToggle if :
                        // - If have been clicked
                        // - If one toggle rate is put to false and event rate field is true
                        // - If all the toggle rates are Check
                        let _eventProductForSale;
                        let isCreateProductEvent = Boolean(!eventProductForSale.id);

                        if (isCreateProductEvent) {
                            _eventProductForSale = { ...eventProductForSale, [field]: checked };
                        } else {
                            _eventProductForSale = {
                                event: eventId,
                                id: eventProductForSale.id,
                                product: productId,
                                [field]: checked
                            };
                        }

                        if (isCreateProductEvent) {
                            setEventRateToggleDisabled(true);
                        }

                        return new ProductSaleModel(_eventProductForSale)
                            .save({
                                organizationId: organization.id,
                                productId: productId
                            })
                            .then(result => {
                                _eventProductForSale[field] = checked;
                                if (isCreateProductEvent) {
                                    // update pk
                                    _eventProductForSale.id = result.response.id;
                                }

                                setEventProductForSale(prev => ({
                                    ...prev,
                                    ...{ [field]: checked }
                                }));
                                toast.success(t('ticket.cross-selling.putForSale.edited'));
                            })
                            .catch(() => {
                                // Event update failed (rates succeeded) we still sync
                                toast.error(t('ticket.cross-selling.add.productEditError'));
                                sync();
                            })
                            .finally(() => {
                                setEventRateToggleDisabled(false);
                            });
                    }
                    // Rates succeeded, but event condition not met
                    toast.success(t('ticket.cross-selling.putForSale.edited'));
                })
                .catch(e => {
                    sync();
                    console.log(e);
                    toast.error(t('ticket.cross-selling.add.productEditError'));
                });
        },
        [ratesProductForSale, eventProductForSale, organization, productId, sync]
    );

    const onToggle = React.useCallback(
        (field, checked, rateId) => {
            postMEV(field, checked, rateId);
        },
        [postMEV]
    );

    const isLoading = React.useMemo(() => {
        return loadingRates || loadingQuotas || loadingEvent || loadForSale;
    }, [loadingRates, loadingEvent, loadForSale]);

    let tableCells = React.useMemo(() => {
        return [
            {
                header: t('ticket.rates.rateName'),
                renderRate: (rate, { categories, groups }) => (
                    <InheritedProperty
                        displayIcon={false}
                        property="name"
                        object={rate}
                        handleProperty={name => (
                            <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                                <RateCategorySeat event={event} rate={rate} categories={categories} />
                                {name}
                                <RateBundleInfo bundleSize={rate.bundle_size || 1} />
                            </div>
                        )}
                        template="rate_template"
                    />
                ),
                renderGroup: (group, onToggle) => {
                    return (
                        <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                            {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                            <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                            <InheritedProperty displayIcon={false} property="name" object={group} handleProperty={name => <b>{name}</b>} template="group_template" />
                        </div>
                    );
                }
            },
            {
                header: t('ticket.cross-selling.putForSale.sellStatus'),
                renderRate: (rate, { categories, groups }) => {
                    return <RateStatus quotas={quotas} rate={rate} settings={event.settings} event={event} />;
                },
                renderGroup: () => {
                    return null;
                }
            },
            {
                header: (
                    <>
                        {t('ticket.cross-selling.putForSale.mandatory')} <TooltipHelp title={t('ticket.cross-selling.putForSale.mandatoryTooltip')} />
                    </>
                ),
                renderRate: (rate, { categories, groups }) => {
                    let rateProductForSale = ratesProductForSale[rate.pk];
                    let field = 'is_mandatory_product';
                    return (
                        <Toggle
                            onChange={(_, { checked }) => onToggle(field, checked, rate.pk)}
                            checked={(rateProductForSale?.id ? rateProductForSale?.[field] : eventProductForSale?.[field]) || false}
                            disabled={disableRateRows.has(rate.pk)}
                        />
                    );
                },
                renderGroup: () => {
                    return null;
                }
            },
            {
                header: (
                    <>
                        {t('ticket.cross-selling.putForSale.unfolded')} <TooltipHelp title={t('ticket.cross-selling.putForSale.unfoldedTooltip')} />
                    </>
                ),
                renderRate: (rate, { categories, groups }) => {
                    let rateProductForSale = ratesProductForSale[rate.pk];
                    let field = 'is_opened_product';
                    return (
                        <Toggle
                            onChange={(_, { checked }) => onToggle(field, checked, rate.pk)}
                            checked={(rateProductForSale?.id ? rateProductForSale?.[field] : eventProductForSale?.[field]) || false}
                            disabled={disableRateRows.has(rate.pk)}
                        />
                    );
                },
                renderGroup: () => {
                    return null;
                }
            },
            {
                header: t('ticket.cross-selling.putForSale.activation'),
                renderRate: (rate, { categories, groups }) => {
                    let rateProductForSale = ratesProductForSale[rate.pk];
                    let field = 'is_product_in_sale';
                    return (
                        <Toggle
                            onChange={(_, { checked }) => onToggle(field, checked, rate.pk)}
                            checked={(rateProductForSale?.id ? rateProductForSale?.[field] : eventProductForSale?.[field]) || false}
                            disabled={disableRateRows.has(rate.pk)}
                        />
                    );
                },
                renderGroup: () => {
                    return null;
                }
            }
        ];
    }, [event, onToggle, ratesProductForSale, disableRateRows]);

    const groupActionToggle = React.useCallback(
        (_, checked, action) => {
            const ids = ratesArray.rates_list.map(rate => rate.id);
            postMEV(action, checked, ids, true);
        },
        [ratesArray, category, productId, ratesProductForSale, event, postMEV]
    );

    const activateAllRates = React.useMemo(() => {
        if (!ratesArray.rates_list || ratesArray.rates_list.length === 0) {
            return null;
        }
        return (
            <TableRow>
                <TableCell>
                    <div className={Style['activate-all-rates-title']}>{t('ticket.cross-selling.putForSale.activateForAll')}</div>
                </TableCell>
                <TableCell />
                <TableCell>
                    <Toggle
                        onChange={(_, { checked }) => groupActionToggle(_, checked, 'is_mandatory_product')}
                        checked={eventProductForSale?.is_mandatory_product}
                        disabled={eventRateToggleDisabled}
                    />
                </TableCell>
                <TableCell>
                    <Toggle
                        onChange={(_, { checked }) => groupActionToggle(_, checked, 'is_opened_product')}
                        checked={eventProductForSale?.is_opened_product}
                        disabled={eventRateToggleDisabled}
                    />
                </TableCell>
                <TableCell>
                    <Toggle
                        onChange={(_, { checked }) => groupActionToggle(_, checked, 'is_product_in_sale')}
                        checked={eventProductForSale?.is_product_in_sale}
                        disabled={eventRateToggleDisabled}
                    />
                </TableCell>
            </TableRow>
        );
    }, [eventRateToggleDisabled, eventProductForSale, groupActionToggle, ratesArray]);

    if (isLoading) return null;

    return (
        <>
            <WeezHeader title={event.name} backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/products/${productId}/activation`) }} />
            <NacreCard separator={false}>
                <RatesManager
                    event={event}
                    organization={organization}
                    initialQuery={{
                        organizationId: organization.id,
                        eventId
                    }}
                    tableCells={tableCells}
                    mode={'product'}
                    selectable={false}
                    activeSyncRates={false}
                    activeCacheRates
                    activeCacheGroups
                    extraRow={activateAllRates}
                />
            </NacreCard>
        </>
    );
}
